import {
  createFirmOfficeV1,
  deleteFirmOfficeV1,
  getFirmOfficesV1,
  updateFirmOfficeV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type FirmOffice } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const getFirmOfficesQueryOptions = () => {
  return {
    queryKey: [queryKeys.firmOffices],
    queryFn: async () => getFirmOfficesV1(),
  };
};

export const useCreateFirmOffice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createFirmOfficeV1>[0]) =>
      createFirmOfficeV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.firmOffices],
        (oldData: APIResponse<FirmOffice[]> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            // Create a temporary id so we can open the new tab
            data: [...oldData.data, { ...data, id: "temp" }],
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.firmOffices]),
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([queryKeys.firmOffices], context.previousData);
      }
      toast.error("Error creating firm office");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmOffices],
      });
    },
    onSuccess: () => {
      toast.success("Firm office created successfully");
    },
  });
};

export const useUpdateFirmOffice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateFirmOfficeV1>[0]) =>
      updateFirmOfficeV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.firmOffices],
        (oldData: APIResponse<FirmOffice[]> | undefined) => {
          if (!oldData) return;

          return {
            data: oldData.data.map((office) =>
              office.id === data.id ? { ...data, sk: data.id } : office,
            ),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.firmOffices]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([queryKeys.firmOffices], context.previousData);
      }
      toastAPIErrorMessage(error, "Error updating firm office");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmOffices],
      });
    },
    onSuccess: () => {
      toast.success("Firm office updated successfully");
    },
  });
};

export const useDeleteFirmOffice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteFirmOfficeV1>[0]) =>
      deleteFirmOfficeV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.firmOffices],
        (oldData: APIResponse<FirmOffice[]> | undefined) => {
          if (!oldData) return;
          return {
            data: oldData.data.filter((office) => office.id !== data.id),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.firmOffices]),
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([queryKeys.firmOffices], context.previousData);
      }
      toast.error("Error deleting firm office");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmOffices],
      });
    },
    onSuccess: () => {
      toast.success("Firm office deleted successfully");
    },
  });
};
